import React, { useState } from 'react';
import { resetSourceStatus } from '../../services/sourceService';
import { Button, Dialog, Toast, Spinner } from '../ui';
import { RefreshCw } from '../ui/icons';

interface IResetStatusButtonProps {
  sourceId: string;
  sourceName: string;
  currentStatus: string;
  onResetComplete?: () => void;
  disabled?: boolean;
}

const ResetStatusButton: React.FC<IResetStatusButtonProps> = ({ 
  sourceId, 
  sourceName,
  currentStatus,
  onResetComplete,
  disabled = false
}) => {
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleReset = async () => {
    setIsResetting(true);
    setError(null);
    setSuccess(null);

    try {
      await resetSourceStatus(sourceId);
      setSuccess('Source status has been reset successfully');
      if (onResetComplete) {
        onResetComplete();
      }
    } catch (err) {
      console.error('Error resetting source status:', err);
      setError('Failed to reset source status');
    } finally {
      setIsResetting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  const handleOpenConfirmDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  // Only show the button if the source is in PROCESSING or FAILED state
  if (currentStatus !== 'PROCESSING' && currentStatus !== 'FAILED') {
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={handleOpenConfirmDialog}
        disabled={isResetting || disabled}
        size="sm"
        className="border-yellow-600 text-yellow-600 hover:bg-yellow-50"
      >
        {isResetting ? (
          <>
            <Spinner size="sm" className="mr-2" />
            Resetting...
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset Status
          </>
        )}
      </Button>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCloseConfirmDialog}
        title="Reset Source Status"
        footer={
          <>
            <Button variant="outline" onClick={handleCloseConfirmDialog} disabled={isResetting}>
              Cancel
            </Button>
            <Button
              onClick={handleReset}
              disabled={isResetting}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              {isResetting ? 'Resetting...' : 'Reset Status'}
            </Button>
          </>
        }
      >
        <p className="text-gray-700 mb-2">
          Are you sure you want to reset the status of "{sourceName}" from "{currentStatus}" to "IDLE"?
        </p>
        <p className="text-sm text-gray-600">
          This will clear any error messages and allow you to restart processing.
        </p>
      </Dialog>

      <Toast
        open={!!error}
        message={error || ''}
        variant="error"
        onClose={handleCloseError}
        autoHideDuration={6000}
      />

      <Toast
        open={!!success}
        message={success || ''}
        variant="success"
        onClose={handleCloseSuccess}
        autoHideDuration={6000}
      />
    </>
  );
};

export default ResetStatusButton; 